import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import {
  createTagDefinition,
  updateTagDefinition,
} from "../../../api/tagDefinitions";
import { TrashIcon } from "@heroicons/react/24/outline";

const COLOR_PRESETS = [
  "#3B82F6",
  "#DC2626",
  "#16A34A",
  "#CA8A04",
  "#9333EA",
  "#0D9488",
  "#EA580C",
  "#6366F1",
];

export function TagDefinitionDialog({
  open,
  onClose,
  tag,
  onSaved,
  onDelete,
}) {
  const { t } = useTranslation();

  const scopeOptions = [
    { value: "", label: t("All modules") },
    { value: "case", label: t("Cases") },
    { value: "project", label: t("Projects") },
    { value: "customer", label: t("Customers") },
    { value: "task", label: t("Tasks") },
  ];

  const initialValues = {
    id: tag?.id,
    name: tag?.name ?? "",
    color: tag?.color ?? "",
    sort_order: tag?.sort_order ?? 0,
    scope_module: tag?.scope_module ?? "case",
    active: tag?.active ?? true,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const payload = {
        name: values.name,
        color: values.color || null,
        sort_order: Number(values.sort_order) || 0,
        scope_module: values.scope_module || null,
        active: values.active,
      };
      const res = values.id
        ? await updateTagDefinition(values.id, payload)
        : await createTagDefinition(payload);
      if (res?.errors) {
        toast.error(res.errors[0] || t("Could not save"));
        setSubmitting(false);
        return;
      }
      onSaved?.(res);
      toast.success(values.id ? t("Saved") : t("Created"));
    } catch (err) {
      toast.error(t("Could not save"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object({
          name: Yup.string().required(t("Name is required")).max(100),
        })}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form>
            <div className="space-y-4">
              <PMField
                name="name"
                type="text"
                label={t("Name")}
                {...formik}
              />
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Scope module")}
                </label>
                <select
                  name="scope_module"
                  value={formik.values.scope_module}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  {scopeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {t("Color")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => formik.setFieldValue("color", c)}
                      className={formik.values.color === c ? "ring-2 ring-blue-500 ring-offset-1" : ""}
                      style={{
                        backgroundColor: c,
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                      }}
                      title={c}
                    />
                  ))}
                </div>
                <input
                  type="text"
                  name="color"
                  value={formik.values.color}
                  onChange={formik.handleChange}
                  placeholder="#3B82F6"
                  className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <PMField
                name="sort_order"
                type="number"
                label={t("Sort order")}
                {...formik}
              />
            </div>

            <div className="mt-6 flex items-center justify-between gap-4">
              <div>
                {tag?.id && onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(tag.id)}
                    className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {t("Delete")}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t("Close")}
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {formik.isSubmitting ? t("Saving") + "…" : tag?.id ? t("Save") : t("Create")}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
}
